import { useEffect, useState, type ReactNode } from 'react';
import { useExtensions } from '../../lib/api/hooks.js';
import { useAdminGroups } from '../../lib/api/hooks-extension.js';
import { SearchInput, Chip, ChipList } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * What a person reaches through the self-service permissions (docs/33 §5): whole departments, and
 * single extensions. Built for accounts with a thousand of each, so nothing long is listed until it
 * is searched for: a department covers its extensions now and later, and a lone seat is found by
 * number or name. Two matching panels, each a search, its matches as a list, and what is chosen as
 * chips (owner, 18 September 2026: the first layout was "a bit sloppy").
 */

export interface Seat { id: string; number: string; display_name: string | null }
export interface Access { departments: { id: string; name: string }[]; extensions: Seat[] }

const SHOWN = 8;

export function AccessPicker({ tenantId, userId, value, onChange }: {
  tenantId: string;
  /** Whose access this is, so their own seats are not shown as taken. Absent on an invitation. */
  userId?: string;
  value: Access;
  onChange: (next: Access) => void;
}) {
  const groups = useAdminGroups(tenantId);
  const [deptQ, setDeptQ] = useState('');
  const [extQ, setExtQ] = useState('');
  const [extSearch, setExtSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setExtSearch(extQ.trim()), 250);
    return () => clearTimeout(t);
  }, [extQ]);
  const found = useExtensions(extSearch ? tenantId : null, { q: extSearch, limit: SHOWN });

  const chosenDept = new Set(value.departments.map((d) => d.id));
  const chosenExt = new Set(value.extensions.map((e) => e.id));
  const allGroups = groups.data?.data ?? [];
  const needle = deptQ.trim().toLowerCase();
  const deptMatches = allGroups
    .filter((g) => !chosenDept.has(g.id))
    .filter((g) => !needle || g.name.toLowerCase().includes(needle) || (g.code ?? '').toLowerCase().includes(needle));
  const covered = allGroups.filter((g) => chosenDept.has(g.id)).reduce((n, g) => n + (g.extension_count ?? 0), 0);
  const extMatches = (found.data?.data ?? []).filter((e) => !chosenExt.has(e.id));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
      <Panel
        title="Departments"
        hint={value.departments.length > 0
          ? `${covered} extension${covered === 1 ? '' : 's'} now, and any added later.`
          : 'Every extension in a department, including ones added later.'}
        chips={value.departments.map((d) => ({ id: d.id, label: d.name }))}
        onRemove={(id) => onChange({ ...value, departments: value.departments.filter((d) => d.id !== id) })}
      >
        {allGroups.length === 0 && !groups.data === undefined ? (
          <div className="arc-subtle" style={{ fontSize: 12.5 }}>This account has no departments.</div>
        ) : (
          <>
            <SearchInput value={deptQ} onChange={setDeptQ} label="Find a department" placeholder="Search departments" />
            <Results
              empty={deptMatches.length === 0 ? (allGroups.length === chosenDept.size ? 'All departments are chosen.' : 'No match.') : null}
              more={deptMatches.length > SHOWN ? deptMatches.length - SHOWN : 0}
            >
              {deptMatches.slice(0, SHOWN).map((g) => (
                <ResultRow key={g.id} onAdd={() => onChange({ ...value, departments: [...value.departments, { id: g.id, name: g.name }] })}
                  primary={g.name} secondary={`${g.extension_count ?? 0} extension${g.extension_count === 1 ? '' : 's'}`} />
              ))}
            </Results>
          </>
        )}
      </Panel>

      <Panel
        title="Extensions"
        hint="Their own seats: a person and their phone."
        mono
        chips={value.extensions.map((e) => ({ id: e.id, label: e.number, detail: e.display_name }))}
        onRemove={(id) => onChange({ ...value, extensions: value.extensions.filter((e) => e.id !== id) })}
      >
        <SearchInput value={extQ} onChange={setExtQ} label="Find an extension" placeholder="Search number or name" />
        {extSearch && (
          <Results
            empty={found.isFetching ? 'Searching…' : extMatches.length === 0 ? 'No match.' : null}
            more={0}
          >
            {extMatches.map((e) => {
              const taken = !!e.user_id && e.user_id !== userId;
              return (
                <ResultRow key={e.id} mono disabled={taken} primary={e.number}
                  secondary={taken ? `${e.display_name ?? ''}${e.display_name ? ' · ' : ''}already somebody’s` : e.display_name ?? ''}
                  onAdd={() => onChange({ ...value,
                    extensions: [...value.extensions, { id: e.id, number: e.number, display_name: e.display_name ?? null }] })} />
              );
            })}
          </Results>
        )}
      </Panel>
    </div>
  );
}

function Panel({ title, hint, chips, onRemove, mono = false, children }: {
  title: string; hint: string; mono?: boolean;
  chips: { id: string; label: string; detail?: string | null }[];
  onRemove: (id: string) => void; children: ReactNode;
}) {
  return (
    <div style={{ border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r)', padding: 12, background: 'var(--arc-surface)' }}>
      <div className="arc-row" style={{ justifyContent: 'space-between', marginBottom: 2 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{title}</span>
        {chips.length > 0 && <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>{chips.length} chosen</span>}
      </div>
      <div className="arc-hint" style={{ margin: '0 0 10px' }}>{hint}</div>
      {chips.length > 0 && (
        <ChipList style={{ marginBottom: 10 }}>
          {chips.map((i) => (
            <Chip key={i.id} mono={mono} {...(i.detail ? { detail: i.detail } : {})}
              onRemove={() => onRemove(i.id)} removeLabel={`Remove ${i.label}`}>
              {i.label}
            </Chip>
          ))}
        </ChipList>
      )}
      <div className="arc-stack" style={{ gap: 6 }}>{children}</div>
    </div>
  );
}

function Results({ empty, more, children }: { empty: string | null; more: number; children: ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)', overflow: 'hidden' }}>
      {empty ? (
        <div className="arc-subtle" style={{ padding: '8px 10px', fontSize: 12.5 }}>{empty}</div>
      ) : children}
      {more > 0 && (
        <div className="arc-subtle" style={{ padding: '6px 10px', fontSize: 12, borderTop: '1px solid var(--arc-border)' }}>
          {more} more: search to narrow the list.
        </div>
      )}
    </div>
  );
}

function ResultRow({ primary, secondary, onAdd, mono = false, disabled = false }: {
  primary: string; secondary: string; onAdd: () => void; mono?: boolean; disabled?: boolean;
}) {
  return (
    <button type="button" className="arc-access-row" disabled={disabled} onClick={onAdd}>
      <span className={mono ? 'arc-mono' : undefined} style={{ fontWeight: 550 }}>{primary}</span>
      <span className="arc-subtle arc-truncate" style={{ flex: 1, textAlign: 'left' }}>{secondary}</span>
      {!disabled && <Icon name="plus" size={13} />}
    </button>
  );
}

/** "Sales, Support · 3 extensions", or null when they reach nothing through self-service. */
export function accessSummary(a: Access): string | null {
  const parts: string[] = [];
  if (a.departments.length > 0) {
    parts.push(a.departments.length <= 2 ? a.departments.map((d) => d.name).join(', ')
      : `${a.departments.length} departments`);
  }
  if (a.extensions.length > 0) {
    parts.push(a.extensions.length <= 3 ? a.extensions.map((e) => e.number).join(', ')
      : `${a.extensions.length} extensions`);
  }
  return parts.length ? parts.join(' · ') : null;
}
